import { useState } from "react";
import { EmailSuggestInput } from "./EmailSuggestInput";

export const LETTER_EMPTY = {
  employee_name: "", employee_id: "", designation: "", department: "", joining_date: "",
  reporting_manager: "", work_location: "Kochi / Company Office", employment_type: "Permanent",
  monthly_gross_salary: "", annual_ctc: "", salary_breakup: "", perks: "",
  probation_days: 180, notice_days: 30, working_hours: "Monday to Saturday, 9:30 AM to 5:30 PM",
  weekly_off: "Sunday", special_conditions: "", letter_date: new Date().toISOString().slice(0, 10),
  personal_email: "", official_email: "",
};
export const EMP_TYPES = ["Permanent", "Temporary", "Contract", "Probationary", "Part-Time", "Outsourced", "Consultant", "Intern", "Trainee", "Casual", "Fixed-Term Contract"];

const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
export const calcBreakup = (gross) => {
  const g = Number(gross);
  if (!g || g <= 0) return "";
  const basic = g * 0.5, hra = g * 0.2, allowances = g - basic - hra;
  return `Basic (50%): ${inr(basic)}\nHRA (20%): ${inr(hra)}\nOther Allowances (30%): ${inr(allowances)}\nMonthly Gross: ${inr(g)}\nNet Pay (approx.): ${inr(g)}`;
};

export const letterPayload = (form) => ({
  ...form, monthly_gross_salary: Number(form.monthly_gross_salary), annual_ctc: Number(form.annual_ctc),
  probation_days: Number(form.probation_days), notice_days: Number(form.notice_days),
  extra_docs: form.extra_docs || [], waived_docs: form.waived_docs || [],
});

const cls = "mt-1 w-full bg-nx-navy2 border border-white/10 px-3 py-2.5 text-sm text-white focus:border-nx-gold/60 outline-none";
const Input = ({ label, required, ...props }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">{label}{required && <span className="text-nx-goldlight"> *</span>}</span>
    {props.type === "email" ? <EmailSuggestInput {...props} wrapClassName="relative w-full" className={cls} /> : <input {...props} className={cls} />}
  </label>
);
const Area = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">{label}</span>
    <textarea rows={4} {...props} className={`${cls} resize-y`} />
  </label>
);

export const LetterFormFields = ({ form, setForm, testPrefix = "letter" }) => {
  const [ctcTouched, setCtcTouched] = useState(false);
  const [breakupTouched, setBreakupTouched] = useState(!!form.salary_breakup);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setGross = (e) => {
    const v = e.target.value, g = Number(v);
    setForm((f) => ({ ...f, monthly_gross_salary: v, ...(ctcTouched ? {} : { annual_ctc: g > 0 ? String(g * 12) : "" }), ...(breakupTouched ? {} : { salary_breakup: calcBreakup(v) }) }));
  };
  const t = (k) => `${testPrefix}-${k}`;
  return (
    <>
      <Input label="Employee Name" required value={form.employee_name} onChange={set("employee_name")} data-testid={t("employee-name")} />
      <Input label="Employee ID (blank = auto)" value={form.employee_id} onChange={set("employee_id")} placeholder="Auto-generated if empty" data-testid={t("employee-id")} />
      <Input label="Designation" required value={form.designation} onChange={set("designation")} data-testid={t("designation")} />
      <Input label="Department" required value={form.department} onChange={set("department")} data-testid={t("department")} />
      <Input label="Joining Date" required type="date" value={form.joining_date} onChange={set("joining_date")} data-testid={t("joining-date")} />
      <Input label="Reporting Manager" required value={form.reporting_manager} onChange={set("reporting_manager")} data-testid={t("manager")} />
      <Input label="Work Location" required value={form.work_location} onChange={set("work_location")} data-testid={t("location")} />
      <label className="block">
        <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">Employment Type <span className="text-nx-goldlight">*</span></span>
        <select required value={form.employment_type} onChange={set("employment_type")} data-testid={t("emp-type")} className={cls}>
          {EMP_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </label>
      <Input label="Monthly Gross Salary (₹)" required type="number" min="1" value={form.monthly_gross_salary} onChange={setGross} data-testid={t("monthly-salary")} />
      <Input label="Annual CTC (₹) — auto = gross × 12" required type="number" min="1" value={form.annual_ctc} onChange={(e) => { setCtcTouched(true); set("annual_ctc")(e); }} data-testid={t("annual-ctc")} />
      <Input label="Probation Period (days)" required type="number" min="0" value={form.probation_days} onChange={set("probation_days")} data-testid={t("probation")} />
      <Input label="Notice Period (days)" required type="number" min="0" value={form.notice_days} onChange={set("notice_days")} data-testid={t("notice")} />
      <Input label="Working Hours" required value={form.working_hours} onChange={set("working_hours")} data-testid={t("hours")} />
      <Input label="Weekly Off" required value={form.weekly_off} onChange={set("weekly_off")} data-testid={t("weekly-off")} />
      <Input label="Letter Date" required type="date" value={form.letter_date} onChange={set("letter_date")} data-testid={t("date")} />
      <Input label="Employee Personal Email" required type="email" value={form.personal_email} onChange={set("personal_email")} placeholder="Given at time of joining" data-testid={t("personal-email")} />
      <Input label="Official Email (@nexsecure.in)" type="email" value={form.official_email} onChange={set("official_email")} placeholder="name@nexsecure.in" data-testid={t("official-email")} />
      <div className="sm:col-span-2 lg:col-span-3 grid sm:grid-cols-3 gap-4">
        <Area label="Salary Break-up (optional — auto-calculated from gross, editable)" rows={9} value={form.salary_breakup} onChange={(e) => { setBreakupTouched(true); set("salary_breakup")(e); }} data-testid={t("breakup")} />
        <Area label="Perks / Benefits (optional)" value={form.perks} onChange={set("perks")} data-testid={t("perks")} />
        <Area label="Special Conditions (optional)" value={form.special_conditions} onChange={set("special_conditions")} data-testid={t("special")} />
      </div>
    </>
  );
};
